import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useReportTileEmpty } from "./DashboardTile";

interface LeaveBalanceItem {
  leaveTypeId: number;
  leaveTypeName: string;
  leaveTypeColor: string;
  quota: number;
  used: number;
  remaining: number;
}

interface LeaveBalanceWidgetProps {
  balances?: LeaveBalanceItem[];
}

export default function LeaveBalanceWidget({ balances: propBalances }: LeaveBalanceWidgetProps) {
  const [, navigate] = useLocation();

  // Fallback: fetch from leave-balances if not provided via dashboard summary
  const { data: fetchedBalances, isLoading } = useQuery<any[]>({
    queryKey: ["/api/leave-balances"],
    enabled: !propBalances,
  });

  // Normalize fetched balances to same shape
  const balances: LeaveBalanceItem[] = propBalances ?? (fetchedBalances || []).map((b: any) => ({
    leaveTypeId: b.leaveTypeId,
    leaveTypeName: b.leaveType?.name || 'Leave',
    leaveTypeColor: b.leaveType?.color || '#6b7280',
    quota: Number(b.totalQuota || b.quota || 0),
    used: Number(b.usedDays || b.used || 0),
    remaining: Number(b.remainingDays || b.remaining || 0),
  }));

  useReportTileEmpty("leave_balance", !isLoading && (balances?.length ?? 0) === 0);

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 shadow-md">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold">Leave Balance</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-950/30 gap-1 font-semibold h-8 px-2"
            onClick={() => navigate("/leave")}
          >
            Details <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {balances.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Calendar className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No leave data available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {balances.slice(0, 4).map((b) => {
              const usedPercent = b.quota > 0 ? Math.min(100, Math.round((b.used / b.quota) * 100)) : 0;
              return (
                <div key={b.leaveTypeId}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: b.leaveTypeColor }} />
                      <span className="text-sm font-medium text-foreground">{b.leaveTypeName}</span>
                    </div>
                    <span className="text-sm font-bold text-foreground">
                      {b.remaining}<span className="text-xs font-normal text-muted-foreground"> / {b.quota}d</span>
                    </span>
                  </div>
                  <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "absolute inset-y-0 left-0 rounded-full transition-all duration-500",
                        usedPercent >= 90 ? "bg-rose-500" : usedPercent >= 70 ? "bg-amber-500" : "bg-sky-500"
                      )}
                      style={{ width: `${usedPercent}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {balances.length > 4 && (
              <p className="text-xs text-muted-foreground text-center">+{balances.length - 4} more leave types</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
